<template>
  <master-layout pageTitle="Manage Team Members">
    <div class="chat-toolbar">
      <div class="toolbar-start">
        <VIcon name="arrow-left-line" :size="-4" @click="() => $router.go(-1)" class="cursorPointer"></VIcon>
        <h2 class="vtext-page-title--regular vml-20">{{ (dummyMember.id ? 'Edit' : 'Add') + ' Member' }}</h2>
      </div>
    </div>
    <div class="gridMargin model-sections width60P">
      <div class="previewHeader">
        <span class="vtext-card-title--light">Team member details</span>
      </div>
      <div class="sectionPadding">
        <div class="flex-sections vmb-20">
          <div class="section_divider">
            <h2 class="vtext-subtitle vpb-10">First name (Max 30 characters)</h2>
            <div class="counter-wrapper">
              <VTextField v-model="dummyMember.firstName" placeholder="First name" aria-label="first name" :maxlength="30" appearance="ghost" class="vivid-input"></VTextField>
              <div class="custom-counter">
                  {{ (dummyMember.firstName || '').length }}/30
              </div>
            </div>
          </div>
          <div class="section_divider">
            <h2 class="vtext-subtitle vpb-10">Last name (Max 30 characters)</h2>
            <div class="counter-wrapper">
              <VTextField v-model="dummyMember.lastName" placeholder="Last name" aria-label="last name" :maxlength="30" appearance="ghost" class="vivid-input"></VTextField>
              <div class="custom-counter">
                  {{ (dummyMember.lastName || '').length }}/30
              </div>
            </div>
          </div>
          <div class="section_divider">
            <h2 class="vtext-subtitle vpb-10">Email</h2>
            <VTextField :disabled="dummyMember.id !== undefined" type="email" @change="updateEmail(dummyMember.email)" aria-label="email" v-model="dummyMember.email" placeholder="Email" appearance="ghost" class="vivid-input"></VTextField>
          </div>
          <div class="section_divider">
            <h2 class="vtext-subtitle vpb-10">Role</h2>
            <selectDropDown
              :options="roleOptions"
              :selectedValueProp="dummyMember.role"
              placeholder="Select Role"
              @onSelectOption="onRoleSelect"
            />
          </div>
        </div>
        <VButton
          class="vvd-btn-primary vvd-btn-block"
          connotation="cta"
          appearance="filled"
          :label="submitLoader ? 'Submitting...' : 'Submit'"
          :disabled="isDisabled || submitLoader"
          @click="saveOrUpdateMember"
        ></VButton>
      </div>
    </div>
  </master-layout>
</template>

<script>
import selectDropDown from '@/components/selectDropDown.vue';
import apiInterceptor from '@/shared/apiInterceptor';
import { queryClient } from '@/shared/queryClient';
import { teamMembersQueryOptions } from '@/composables/useTeamMembers';
import { businessRolesQueryOptions } from '@/composables/useBusinessRoles';
import { useUIStore } from '@/stores/ui';
import { validateEmail } from '@/helpers/validation.helpers';
import { environment } from '@/shared/env';

export default {
  components: {
    selectDropDown
  },
  data() {
    return {
      allRoles: [],
      dummyMember: {
        admin: false,
        businessroleid: undefined,
        email: '',
        firstName: '',
        id: undefined,
        lastName: '',
        name: undefined,
        role: '',
      },
      submitLoader: false
    }
  },
  watch: {
    // Re-init on every navigation to this route (fresh Add, or Edit via
    // ?memberid). init() resets the form first, so re-opening "Add member"
    // never shows the previously entered/edited member's data. `immediate`
    '$route': {
      handler() {
        this.init();
      },
      immediate: true,
    },
  },
  computed: {
    isEdit() {
      return this.dummyMember.id !== undefined;
    },
    isDisabled(){
      return !this.dummyMember.firstName || !this.dummyMember.lastName || !this.dummyMember.email || !this.dummyMember.role
    },
    roleOptions() {
      return this.allRoles.map(role => ({
        id: role.name,
        value: role.name,
        title: role.name
      }));
    }
  },
  methods: {
    onRoleSelect({ selectedValue }) {
      this.dummyMember.role = selectedValue;
    },
    async init() {
      this.resetMembersData();
      try {
        useUIStore().updateLoading({ isLoading: true });
        this.allRoles = await queryClient.ensureQueryData(businessRolesQueryOptions);
        if(this.$route.query?.memberid) {
          const allMembers = await queryClient.fetchQuery({ ...teamMembersQueryOptions, staleTime: 0 });
          const selectedMember = allMembers.find(e => e.id == this.$route.query.memberid);
          if (selectedMember) {
            this.dummyMember = { ...selectedMember };
          }
        }
      } catch (error) {
        console.error("Exception", error);
        useUIStore().updateToast({ open: true, message: "Something went wrong", type: "danger" })
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async saveOrUpdateMember() {
      try {
        const { firstName, lastName, role, email, id } = this.dummyMember;

        if (!firstName?.trim()) {
          return useUIStore().updateToast({ open: true, message: "Please enter first name", type: "warning" });
        }

        if (!lastName?.trim()) {
          return useUIStore().updateToast({ open: true, message: "Please enter last name", type: "warning" });
        }

        if (!role) {
          return useUIStore().updateToast({ open: true, message: "Please assign a role", type: "warning" });
        }

        const thisRole = this.allRoles.find(e => e.name === role);
        if (thisRole?.id) {
          this.dummyMember.businessroleid = thisRole.id;
        } else {
          return useUIStore().updateToast({ open: true, message: "Something went wrong", type: "warning" });
        }

        if (!id && !validateEmail(email)) {
          return useUIStore().updateToast({ open: true, message: "Please enter a valid email ID", type: "danger" });
        }
        this.submitLoader = true;

        const reqBody = {
          first_name: this.dummyMember.firstName.trim(),
          last_name: this.dummyMember.lastName.trim(),
          email: this.dummyMember.email?.toLowerCase(),
          businessroleid: this.dummyMember.businessroleid,
        };
        const apiUrl = `${environment("VUE_APP_API_URL")}roles/v1/team_members/`;
        const apiCall = this.isEdit ? apiInterceptor.patch : apiInterceptor.post;

        await apiCall(apiUrl, reqBody);
        queryClient.invalidateQueries({ queryKey: ['team-members'] });
        useUIStore().updateToast({ open: true, message: this.isEdit ? "Member updated successfully!" : "Member added successfully!", type: "success" });
        // Clear the form before leaving so a cached instance doesn't prefill the
        // next "Add member" with this member's data.
        this.resetMembersData();
        this.$router.push({ path: '/settings/team' });
      } catch (error) {
        useUIStore().updateToast({ open: true, message: error?.message || "Something went wrong!", type: "danger" });
      } finally {
        this.submitLoader = false;
      }
    },
    updateEmail(email){
      if (!validateEmail(email)) {
        useUIStore().updateToast({ open: true, message: "Please enter a valid email ID", type: "danger" });
        return;
      }
    },
    resetMembersData(){
      this.dummyMember = { admin: false, businessroleid: undefined, email: '', firstName: '', id: undefined, lastName: '', name: undefined, role: '' }
    }
  }
}
</script>

<style lang="scss" scoped src="@/resources/css/settings.scss"> </style>

<style scoped>
.chat-toolbar {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--white);
  border-bottom: 1px solid var(--background-primary);
}

.toolbar-start {
  display: flex;
  align-items: center;
}

.vivid-input {
  color: var(--black);
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}
</style>